import type { GeoLevel } from '../types';
import type { ImpactCounty, ImpactEffect, ImpactResult } from '../useImpact';
import { beaLabel } from './bea-industries';
import { countiesForState } from './us-counties';

// Deterministic demo payload for the "Demo data" toggle — the live MRIO API is
// VPN-only and intermittently warming, so the page needs believable numbers
// without a network. Values are seeded from fips+bea so a given scenario always
// renders the same charts. Mirrors the real response: a single county
// investment ripples out across the same state's counties (so the top-counties
// card and the county heat map populate), and `induced` is always 0 (the MRIO
// has no household block yet).

const hash01 = (key: string): number => {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 1_000_000) / 1_000_000;
};

// Supply-chain / consumer industries the pinned investment ripples into (valid
// BEA codes so beaLabel resolves their titles, same as a live response).
const RIPPLE_CODES = [
  '531ORE',
  '722110',
  '531HSO',
  '424700',
  '622000',
  '561300',
  '541100',
  '484000',
];

// How many counties a scenario's impact spreads across.
const DEMO_COUNTY_SPREAD = 6;

interface DemoRegion {
  fips: string;
  // 0..1 share of the scenario's impact landing in this region.
  weight: number;
}

const normalize = (raw: { fips: string; weight: number }[]): DemoRegion[] => {
  const sum = raw.reduce((acc, r) => acc + r.weight, 0) || 1;
  return raw.map((r) => ({ fips: r.fips, weight: r.weight / sum }));
};

// Resolve the regions a scenario's impact lands in. County grain → the chosen
// county takes the lion's share, a few same-state counties pick up spill.
// State grain (kept for when the API supports it) → that state's first N
// counties with a decaying weight.
function demoRegions(fips: string, level: GeoLevel): DemoRegion[] {
  const stateCounties = countiesForState(fips.slice(0, 2));
  if (level === 'county') {
    const others = stateCounties
      .filter((county) => county.fips !== fips)
      .slice(0, DEMO_COUNTY_SPREAD - 1);
    return normalize([
      { fips, weight: 1 },
      ...others.map((county, i) => ({
        fips: county.fips,
        weight: 1 / (i + 2.2),
      })),
    ]);
  }
  const counties = stateCounties.slice(0, DEMO_COUNTY_SPREAD);
  if (counties.length === 0) return [{ fips, weight: 1 }];
  return normalize(
    counties.map((county, i) => ({ fips: county.fips, weight: 1 / (i + 1.4) }))
  );
}

const effect = (
  code: string,
  direct: number,
  indirect: number
): ImpactEffect => ({
  code,
  label: beaLabel(code),
  direct,
  indirect,
  induced: 0,
  total: direct + indirect,
});

const sumEffects = (effects: ImpactEffect[]) =>
  effects.reduce(
    (acc, e) => {
      acc.direct += e.direct;
      acc.indirect += e.indirect;
      acc.total += e.total;
      return acc;
    },
    { direct: 0, indirect: 0, induced: 0, total: 0 }
  );

export interface DemoScenario {
  fips: string;
  level: GeoLevel;
  industryId: string;
  amount: number;
  /** Demo-only re-roll seed (e.g. the output geography). */
  seed?: string;
}

export function buildDemoImpact(scenario: DemoScenario): ImpactResult {
  const { fips, level, industryId: bea, amount, seed } = scenario;
  const seedStr = `${fips}|${bea}|${seed ?? ''}`;
  const jitter = (key: string, lo: number, hi: number) =>
    lo + (hi - lo) * hash01(`${seedStr}|${key}`);

  const counties: ImpactCounty[] = demoRegions(fips, level).map(
    (region, index) => {
      const isPrimary = index === 0;
      const w = region.weight;
      const effects: ImpactEffect[] = [];
      // The investment (direct) lands entirely in the chosen county; every
      // county picks up a weighted share of the indirect supply-chain ripple.
      effects.push(
        effect(
          bea,
          isPrimary ? amount : 0,
          amount * w * jitter('pin-ind', 0.05, 0.12)
        )
      );
      for (const code of RIPPLE_CODES) {
        if (code === bea) continue;
        effects.push(effect(code, 0, amount * w * jitter(code, 0.02, 0.1)));
      }
      const totals = sumEffects(effects);
      return {
        fips: region.fips,
        ...totals,
        industries: effects.sort((a, b) => b.total - a.total),
      };
    }
  );

  // Headline = the chosen county's own impact (its top-counties row), NOT the
  // economy-wide sum — mirrors the live response (see useImpact parseResponse).
  // Primary industries are that county's industries.
  const primary = counties[0];
  return {
    total: primary
      ? {
          direct: primary.direct,
          indirect: primary.indirect,
          induced: primary.induced,
          total: primary.total,
          multiplier: primary.direct > 0 ? primary.total / primary.direct : 0,
        }
      : { direct: 0, indirect: 0, induced: 0, total: 0, multiplier: 0 },
    industries: primary ? [...primary.industries] : [],
    counties: counties.sort((a, b) => b.total - a.total),
  };
}
